/**
 * Photo upload widget for the Shopify product page.
 *
 * Uploads the customer's pet photo to the backend BEFORE add-to-cart, and
 * stores the returned upload_id as a hidden line item property
 * (_photo_upload_id) that the orders/paid webhook reads to trigger
 * generation. Validates image dimensions and aspect ratio client-side before
 * uploading, with a server-side safety net in the backend. Optionally lets
 * the customer pick a physical print add-on (_print_option) — see
 * backend/app/print_options.py for the valid keys; defaults to "digital"
 * (no physical print) if omitted.
 *
 * The delivery email language is no longer picked in this widget — it now
 * comes from Shopify's native language/market selector (customer_locale on
 * the order). This widget's own status text (uploading/success/error, the
 * selection summary, preview messages) is read from a JSON blob emitted by
 * buy-buttons.liquid ([data-ronys-i18n]) via Shopify's `| t` filter, so it
 * always matches the storefront locale — independent of that backend email
 * locale.
 */
(function () {
  const BACKEND_UPLOAD_URL = "https://ronys-backend-144174532283.us-central1.run.app/uploads";
  const BACKEND_PREVIEW_URL = "https://ronys-backend-144174532283.us-central1.run.app/previews";
  const BACKEND_PREVIEW_EMAIL_URL = "https://ronys-backend-144174532283.us-central1.run.app/previews/email";
  const MIN_DIMENSION = 500;
  const MAX_LANDSCAPE_RATIO = 1.5;

  // Lets a customer close the tab mid-design and pick up where they left off
  // (same device, no login) — one saved boceto per product, keyed so a Horse
  // Portrait design never overwrites a Cat Portrait one. The upload_id it
  // references is a GCS object with no TTL (see StorageService), so it's
  // still valid whenever the customer comes back within the window below.
  const BOCETO_STORAGE_PREFIX = "ronysSavedBoceto:";
  const BOCETO_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

  // User-facing strings come from a JSON blob emitted by buy-buttons.liquid
  // ([data-ronys-i18n]), so they follow the storefront's Shopify locale
  // (es/en) instead of being hardcoded here. Parsed once per widget.
  function readI18n(rootEl) {
    const script = rootEl.querySelector('[data-ronys-i18n]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      return {};
    }
  }

  function getImageDimensions(file) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });
  }

  // Matches Shopify's own money.js algorithm so amounts computed in JS render
  // with the exact same symbol/decimal convention as the rest of the store
  // (the variant price, an extra's price) — mixing that with a generic Intl
  // formatter produced a mismatched-looking "54,99 €" next to the store's
  // actual "€54,99" style. `moneyFormat` is each caller's own
  // rootEl.dataset.ronysMoneyFormat (shop.money_format).
  function formatMoney(cents, moneyFormat) {
    function withDelimiters(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision);
      const parts = number.split(".");
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const frac = parts[1] ? decimal + parts[1] : "";
      return whole + frac;
    }
    const match = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const kind = match ? match[1] : "amount";
    let value;
    switch (kind) {
      case "amount_no_decimals":
        value = withDelimiters(cents, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        value = withDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = withDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_space_separator":
        value = withDelimiters(cents, 2, " ", ",");
        break;
      case "amount_with_period_and_space_separator":
        value = withDelimiters(cents, 2, " ", ".");
        break;
      default:
        value = withDelimiters(cents, 2, ",", ".");
    }
    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  // Inverse of formatMoney -- reads a rendered money string (whatever
  // decimal/thousands convention the store's format uses) back into cents, so
  // a price already on the page (the variant price, an extra's price) can be
  // added to another without re-fetching anything. The decimal separator is
  // whichever of "," or "." appears last in the string -- works for every one
  // of formatMoney's five formats above.
  function parseMoneyToCents(text) {
    const cleaned = (text || "").replace(/[^\d.,]/g, "");
    if (!cleaned) return 0;
    const lastComma = cleaned.lastIndexOf(",");
    const lastDot = cleaned.lastIndexOf(".");
    const decimalIndex = Math.max(lastComma, lastDot);
    if (decimalIndex === -1) return Math.round(parseFloat(cleaned) * 100) || 0;
    const intPart = cleaned.slice(0, decimalIndex).replace(/[.,]/g, "");
    const fracPart = cleaned.slice(decimalIndex + 1).replace(/[.,]/g, "");
    return Math.round(parseFloat(`${intPart || "0"}.${fracPart || "0"}`) * 100) || 0;
  }

  // The customer picks Material (Digital/Poster/Lienzo), then — for physical
  // finishes — Marco (Sin marco/Con marco), then a frame color if framed.
  // Those three radios combine into ONE flat "finish" string (see
  // computeFinishValue below) that drives everything downstream exactly like
  // the old single flat radiogroup did: the Shopify variant lookup, the
  // `_print_option` line-item property sent to the backend (must match a key
  // in backend/app/print_options.py), labels, and the frame preview overlay.
  // Frame color never changes price, so it's not part of the Shopify variant
  // — only Material x Marco is.
  const MATERIAL_TO_VARIANT_OPTION = {
    digital: "Digital",
    poster: "Poster",
    canvas: "Lienzo",
  };
  const MARCO_TO_VARIANT_OPTION = {
    sin_marco: "Sin marco",
    con_marco: "Con marco",
  };

  function initPhotoUploadWidget(rootEl) {
    const t = readI18n(rootEl);

    // Base finish + frame color, used to build the "Framed - black - 45x60cm"
    // selection summary. Color is only shown for framed options. Labels are
    // localized via the [data-ronys-i18n] blob.
    const FINISH_BASE_LABEL = {
      digital: t.summaryDigital,
      poster: t.summaryPoster,
      canvas: t.summaryCanvas,
      framed_paper_black: t.summaryFramedPaper,
      framed_paper_natural_wood: t.summaryFramedPaper,
      framed_paper_dark_wood: t.summaryFramedPaper,
      framed_canvas_black: t.summaryFramedCanvas,
      framed_canvas_natural_wood: t.summaryFramedCanvas,
      framed_canvas_dark_wood: t.summaryFramedCanvas,
    };
    const FINISH_COLOR_LABEL = {
      framed_paper_black: t.colorBlack,
      framed_paper_natural_wood: t.colorNaturalWood,
      framed_paper_dark_wood: t.colorDarkWood,
      framed_canvas_black: t.colorBlack,
      framed_canvas_natural_wood: t.colorNaturalWood,
      framed_canvas_dark_wood: t.colorDarkWood,
    };

    const fileInput = rootEl.querySelector('[data-ronys-photo-input]');
    const statusEl = rootEl.querySelector('[data-ronys-photo-status]');
    const hiddenField = rootEl.querySelector('[data-ronys-upload-id-field]');
    const printOptionField = rootEl.querySelector('[data-ronys-print-option-field]');
    const skipPhotoButton = rootEl.querySelector('[data-ronys-skip-photo]');
    const skipPhotoNote = rootEl.querySelector('[data-ronys-skip-photo-note]');
    const photoPendingField = rootEl.querySelector('[data-ronys-photo-pending-field]');
    const materialInputs = Array.from(rootEl.querySelectorAll('[data-ronys-material-input]'));
    const marcoInputs = Array.from(rootEl.querySelectorAll('[data-ronys-marco-input]'));
    const colorInputs = Array.from(rootEl.querySelectorAll('[data-ronys-color-input]'));
    const marcoGroup = rootEl.querySelector('[data-ronys-marco-group]');
    const colorGroup = rootEl.querySelector('[data-ronys-color-group]');
    const sizeInputs = Array.from(rootEl.querySelectorAll('[data-ronys-size-input]'));
    const sizeGroup = rootEl.querySelector('[data-ronys-size-group]');

    // "Together Forever" / any future multi-pet-in-one-scene product (see
    // buy-buttons.liquid's ronys_is_multipet). Changes three things: the
    // print_option sent to the backend gets an "h" suffix (horizontal Gelato
    // catalog, see print_options.py's _HOR_SIZES), extra photo-upload rows
    // become available below the existing single dropzone (which still
    // handles "pet #1" unchanged), and the AI live-preview step is skipped
    // entirely (its markup isn't even rendered for this product -- see the
    // Liquid template -- so every previewGenerateButton/etc reference below
    // is already null and safely no-ops via this file's existing
    // `if (x) x...` guards).
    const isMultipet = rootEl.dataset.ronysIsMultipet === "true";
    const extraUploadIdsField = rootEl.querySelector('[data-ronys-extra-upload-ids-field]');
    const multipetRowsContainer = rootEl.querySelector('[data-ronys-multipet-rows]');
    const multipetAddButton = rootEl.querySelector('[data-ronys-multipet-add]');

    // Combines the Material + Marco + (color, if framed) radios into the same
    // flat string the old single radiogroup used to produce directly (e.g.
    // "digital", "poster", "framed_paper_black", "framed_canvas_black") — see
    // the MATERIAL_TO_VARIANT_OPTION comment above for why this stays flat.
    function computeFinishValue() {
      const material = materialInputs.find((el) => el.checked);
      const materialValue = material ? material.value : "digital";
      if (materialValue === "digital") return "digital";

      const marco = marcoInputs.find((el) => el.checked);
      const marcoValue = marco ? marco.value : "sin_marco";
      if (marcoValue === "sin_marco") return materialValue; // "poster" or "canvas"

      const color = colorInputs.find((el) => el.checked);
      const colorValue = color ? color.value : "black";
      const framedPrefix = materialValue === "poster" ? "framed_paper" : "framed_canvas";
      return `${framedPrefix}_${colorValue}`;
    }

    // Reverse of computeFinishValue, used to restore a saved boceto's finish
    // (stored as the flat string) back into the three separate radio groups.
    function applyFinishValue(finishValue) {
      if (!finishValue || finishValue === "digital") {
        const digitalInput = materialInputs.find((el) => el.value === "digital");
        if (digitalInput) digitalInput.checked = true;
        return;
      }
      if (finishValue === "poster" || finishValue === "canvas") {
        const materialInput = materialInputs.find((el) => el.value === finishValue);
        if (materialInput) materialInput.checked = true;
        const sinMarcoInput = marcoInputs.find((el) => el.value === "sin_marco");
        if (sinMarcoInput) sinMarcoInput.checked = true;
        return;
      }
      const match = /^framed_(paper|canvas)_(.+)$/.exec(finishValue);
      if (!match) return;
      const materialValue = match[1] === "paper" ? "poster" : "canvas";
      const colorValue = match[2];
      const materialInput = materialInputs.find((el) => el.value === materialValue);
      if (materialInput) materialInput.checked = true;
      const conMarcoInput = marcoInputs.find((el) => el.value === "con_marco");
      if (conMarcoInput) conMarcoInput.checked = true;
      const colorInput = colorInputs.find((el) => el.value === colorValue);
      if (colorInput) colorInput.checked = true;
    }

    // Marco is meaningless for Digital (hidden + forced back to "sin_marco"),
    // and color only matters once Marco="Con marco" is picked.
    //
    // "Con marco" + Lienzo is additionally hidden for multipet products only:
    // no horizontal framed_canvas SKU exists in print_options.py (see its
    // catalog comment), so that combination has no valid Gelato product —
    // unlike the single-pet product, where framed_canvas (vertical) is a
    // real, working line. Hiding it here (rather than letting the customer
    // pick it and hit a disabled Add to Cart with no explanation) keeps the
    // widget from ever offering a print that can't actually be fulfilled.
    function syncGroupVisibility() {
      const material = materialInputs.find((el) => el.checked);
      const materialValue = material ? material.value : "digital";
      const isDigital = materialValue === "digital";
      if (marcoGroup) marcoGroup.hidden = isDigital;
      if (isDigital) {
        const sinMarcoInput = marcoInputs.find((el) => el.value === "sin_marco");
        if (sinMarcoInput) sinMarcoInput.checked = true;
      }

      const conMarcoInput = marcoInputs.find((el) => el.value === "con_marco");
      const conMarcoLabel = conMarcoInput ? conMarcoInput.closest("label") : null;
      const conMarcoUnavailable = isMultipet && materialValue === "canvas";
      if (conMarcoLabel) conMarcoLabel.hidden = conMarcoUnavailable;
      if (conMarcoUnavailable && conMarcoInput && conMarcoInput.checked) {
        const sinMarcoInput = marcoInputs.find((el) => el.value === "sin_marco");
        if (sinMarcoInput) sinMarcoInput.checked = true;
      }

      const marco = marcoInputs.find((el) => el.checked);
      const isFramed = !isDigital && marco && marco.value === "con_marco";
      if (colorGroup) colorGroup.hidden = !isFramed;
    }
    const deliveryHint = rootEl.querySelector('[data-ronys-delivery-hint]');
    const posterPackagingNote = rootEl.querySelector('[data-ronys-poster-packaging-note]');
    // Lives in main-product.liquid's price block, outside this widget's own
    // root -- same document-level lookup pattern as addToCartButton/
    // consentInput below, not rootEl.querySelector.
    const deliverySummaryEl = document.querySelector('[data-ronys-delivery-summary]');
    const deliverySummaryTextEl = deliverySummaryEl
      ? deliverySummaryEl.querySelector('[data-ronys-delivery-summary-text]')
      : null;
    const addToCartButton = document.querySelector('[data-ronys-add-to-cart]');
    // The product form renders with novalidate (Dawn's own error-message UI
    // replaces native browser validation), which makes the checkbox's own
    // `required` attribute inert — nothing stops an unchecked add-to-cart
    // without this explicit gate.
    const consentInput = document.querySelector('[data-ronys-consent-input]');
    const previewImg = rootEl.querySelector('[data-ronys-photo-preview]');
    const removeButton = rootEl.querySelector('[data-ronys-photo-remove]');
    const aiPreviewStatus = rootEl.querySelector('[data-ronys-preview-status]');
    const previewGenerateButton = rootEl.querySelector('[data-ronys-preview-generate]');
    // Shown right under the generate-preview button, in the widget's own
    // flow — the gallery overlay further up the page (updateMediaPreview
    // below) is hidden on mobile, where it would otherwise appear above this
    // whole form. See the [data-ronys-media-preview-overlay] mobile rule.
    const inlinePreviewWrap = rootEl.querySelector('[data-ronys-inline-preview]');
    const inlinePreviewImg = rootEl.querySelector('[data-ronys-inline-preview-img]');
    const emailBocetoRow = rootEl.querySelector('[data-ronys-email-boceto-row]');
    const emailBocetoInput = rootEl.querySelector('[data-ronys-email-boceto-input]');
    const emailBocetoSubmit = rootEl.querySelector('[data-ronys-email-boceto-submit]');
    const emailBocetoStatus = rootEl.querySelector('[data-ronys-email-boceto-status]');
    const petNameInput = document.querySelector('[name="properties[_pet_name]"]');
    const extraInputs = Array.from(document.querySelectorAll('[data-ronys-extra-input]'));
    const extraDigitalWrap = document.querySelector('[data-ronys-extra-digital-wrap]');
    const productTitle = rootEl.dataset.ronysProductTitle || "";
    const dedicationInput = rootEl.querySelector('[data-ronys-dedication-input]');
    const fontInputs = Array.from(rootEl.querySelectorAll('[data-ronys-font-input]'));
    const noTextInput = rootEl.querySelector('[data-ronys-no-text-input]');
    const personalizeFields = rootEl.querySelector('[data-ronys-personalize-fields]');
    const personalizeCleanNote = rootEl.querySelector('[data-ronys-personalize-clean-note]');
    const textOverlay = rootEl.querySelector('[data-ronys-text-overlay]');
    const textOverlayName = rootEl.querySelector('[data-ronys-text-overlay-name]');
    const textOverlayDedication = rootEl.querySelector('[data-ronys-text-overlay-dedication]');
    const moneyFormat = rootEl.dataset.ronysMoneyFormat || "€{{amount}}";
    const portraitStyle = rootEl.dataset.ronysStyle || "watercolor";
    // Set only for species-specific products (Horse/Bird/Rabbit/Cat Portrait);
    // empty for the generic "Pet Portrait" product, where any pet is valid.
    const portraitSpecies = rootEl.dataset.ronysSpecies || "";
    const bocetoStorageKey = BOCETO_STORAGE_PREFIX + (rootEl.dataset.ronysProductId || "default");

    function saveBoceto(previewDataUrl) {
      if (!hiddenField.value) return;
      const size = sizeInputs.find((el) => el.checked);
      const payload = {
        uploadId: hiddenField.value,
        previewDataUrl,
        finish: computeFinishValue(),
        size: size ? size.value : null,
        savedAt: Date.now(),
      };
      try {
        window.localStorage.setItem(bocetoStorageKey, JSON.stringify(payload));
      } catch (err) {
        // localStorage full/unavailable — saved-boceto restore is optional, ignore.
      }
    }

    function clearSavedBoceto() {
      try {
        window.localStorage.removeItem(bocetoStorageKey);
      } catch (err) {
        // ignore
      }
    }

    function loadSavedBoceto() {
      let raw;
      try {
        raw = window.localStorage.getItem(bocetoStorageKey);
      } catch (err) {
        return null;
      }
      if (!raw) return null;
      try {
        const saved = JSON.parse(raw);
        if (!saved || !saved.uploadId || !saved.previewDataUrl) return null;
        if (Date.now() - saved.savedAt > BOCETO_MAX_AGE_MS) {
          clearSavedBoceto();
          return null;
        }
        return saved;
      } catch (err) {
        return null;
      }
    }

    // The preview replaces the main product gallery image, which lives in a
    // different part of the DOM (sibling column), not inside rootEl.
    const productRoot = rootEl.closest("product-component") || document;
    const mediaWrapper = productRoot.querySelector('[data-ronys-media-wrapper]');
    const mediaOverlayWrap = productRoot.querySelector('[data-ronys-media-preview-overlay]');
    const mediaOverlayFlatImg = productRoot.querySelector('[data-ronys-media-preview-flat]');
    const mediaOverlayFrameStage = productRoot.querySelector('[data-ronys-media-preview-frame-stage]');
    const mediaOverlayFrameImg = productRoot.querySelector('[data-ronys-media-preview-frame-img]');
    const mediaOverlayFramePhotoImg = productRoot.querySelector('[data-ronys-media-preview-frame-photo]');

    const form = rootEl.closest("form");
    const variantIdField = form ? form.querySelector(".product-variant-id") : null;
    const variantMapScript = rootEl.querySelector('[data-ronys-variant-map]');
    let variantMap = {};
    try {
      variantMap = variantMapScript ? JSON.parse(variantMapScript.textContent) : {};
    } catch (err) {
      variantMap = {};
    }
    const frameTemplatesScript = rootEl.querySelector('[data-ronys-frame-templates]');
    let frameTemplates = {};
    try {
      frameTemplates = frameTemplatesScript ? JSON.parse(frameTemplatesScript.textContent) : {};
    } catch (err) {
      frameTemplates = {};
    }
    const priceTarget = document.getElementById(rootEl.dataset.ronysPriceTarget || "");
    const priceEl = priceTarget ? priceTarget.querySelector(".price-item--regular, .price-item--sale") : null;
    const selectionSummary = rootEl.querySelector('[data-ronys-selection-summary]');
    const plaqueMedium = rootEl.querySelector('[data-ronys-plaque-medium]');

    if (!fileInput || !hiddenField) return;

    let hasUploadedPhoto = false;
    // Customer explicitly chose "complete my order now, send the photo
    // later" instead of uploading. Lets Add to Cart unlock with no photo at
    // all -- see updateSubmitState below. Cleared the moment a real upload
    // succeeds (or a fresh file is picked), so the two states never coexist.
    let photoSkipped = false;
    let variantAvailable = true;
    // Tracks whether the customer's current upload has actually made it into a
    // cart line item. A photo can be uploaded (and the finish/size configured)
    // without ever clicking "add to cart" — if they then click "view cart" they
    // navigate away and lose all of it, since nothing was persisted server-side.
    let addedCurrentUploadToCart = false;
    // Declared here (not further down near requestPreview) because
    // updatePrintOption() calls updateMediaPreview() synchronously during
    // initial setup, before requestPreview's own section would normally run —
    // a `let` declared after that point throws a temporal-dead-zone
    // ReferenceError the moment the page loads.
    let previewRequestToken = 0;
    let currentPreviewDataUrl = null;

    function updateSubmitState() {
      const consentGiven = !consentInput || consentInput.checked;
      if (addToCartButton) {
        addToCartButton.disabled = !((hasUploadedPhoto || photoSkipped) && variantAvailable && consentGiven);
      }
    }

    if (consentInput) {
      consentInput.addEventListener('change', updateSubmitState);
    }

    if (skipPhotoButton) {
      skipPhotoButton.addEventListener('click', () => {
        photoSkipped = true;
        if (photoPendingField) photoPendingField.value = 'true';
        if (skipPhotoNote) {
          skipPhotoNote.textContent = t.skipPhotoActive || '';
          skipPhotoNote.hidden = false;
        }
        updateSubmitState();
      });
    }

    function updatePrintOption() {
      if (!printOptionField) return;
      syncGroupVisibility();
      const finishValue = computeFinishValue();
      const material = materialInputs.find((el) => el.checked);
      const materialValue = material ? material.value : "digital";
      const variantOption1 = MATERIAL_TO_VARIANT_OPTION[materialValue] || "Digital";

      let variantOption2 = "Sin marco";
      let variantOption3 = "Único";
      if (finishValue === "digital") {
        if (sizeGroup) sizeGroup.hidden = true;
        if (deliveryHint) deliveryHint.hidden = true;
        if (posterPackagingNote) posterPackagingNote.hidden = true;
        if (deliverySummaryTextEl && deliverySummaryEl) {
          deliverySummaryTextEl.textContent = deliverySummaryEl.dataset.textDigital || "";
        }
        // Already buying the digital file as the base product -- the paid
        // digital-extra add-on would be redundant, so hide and clear it.
        if (extraDigitalWrap) {
          extraDigitalWrap.hidden = true;
          const digitalCheckbox = extraDigitalWrap.querySelector('[data-ronys-extra-input]');
          if (digitalCheckbox) digitalCheckbox.checked = false;
        }
        printOptionField.value = "digital";
      } else {
        if (extraDigitalWrap) extraDigitalWrap.hidden = false;
        if (sizeGroup) sizeGroup.hidden = false;
        if (deliveryHint) deliveryHint.hidden = false;
        if (deliverySummaryTextEl && deliverySummaryEl) {
          deliverySummaryTextEl.textContent = deliverySummaryEl.dataset.textPhysical || "";
        }
        // Only the unframed poster ships as a flat/rolled print on its own --
        // canvas and every framed option arrive ready to hang, so the note
        // about how the print itself is packaged doesn't apply to them.
        if (posterPackagingNote) posterPackagingNote.hidden = finishValue !== "poster";
        const marco = marcoInputs.find((el) => el.checked);
        const marcoValue = marco ? marco.value : "sin_marco";
        variantOption2 = MARCO_TO_VARIANT_OPTION[marcoValue] || "Sin marco";
        const size = sizeInputs.find((el) => el.checked);
        const sizeValue = size ? size.value : (sizeInputs[0] && sizeInputs[0].value);
        // "h" suffix for the horizontal Gelato catalog (print_options.py's
        // _HOR_SIZES, e.g. "canvas_45x60h") -- the Shopify variant's own
        // "Tamaño" option value stays the plain size ("45x60") either way,
        // see variantOption3 below.
        const printOptionSizeSuffix = isMultipet ? "h" : "";
        printOptionField.value = sizeValue ? `${finishValue}_${sizeValue}${printOptionSizeSuffix}` : finishValue;
        variantOption3 = sizeValue || "Único";
      }

      const variant = variantMap[`${variantOption1}|${variantOption2}|${variantOption3}`];
      if (variant) {
        if (variantIdField) variantIdField.value = variant.id;
        if (priceEl) priceEl.textContent = variant.price;
        variantAvailable = variant.available;
      } else {
        variantAvailable = false;
      }

      if (selectionSummary) {
        const parts = [FINISH_BASE_LABEL[finishValue] || finishValue];
        const color = FINISH_COLOR_LABEL[finishValue];
        if (color) parts.push(color);
        if (finishValue !== "digital") {
          const size = sizeInputs.find((el) => el.checked);
          if (size) parts.push(`${size.value}cm`);
        }
        if (variant) {
          // Checked, visible paid extras (priority review / extra digital
          // file -- see buy-buttons.liquid's .ronys-extras) aren't part of
          // the variant's own price, so they're folded in here too.
          const extrasCents = extraInputs
            .filter((el) => el.checked && !el.closest("[hidden]"))
            .reduce((sum, el) => {
              const priceEl2 = el.closest(".ronys-extra")?.querySelector(".ronys-extra__price");
              return sum + (priceEl2 ? parseMoneyToCents(priceEl2.textContent) : 0);
            }, 0);
          const totalCents = parseMoneyToCents(variant.price) + extrasCents;
          parts.push(formatMoney(totalCents, moneyFormat));
        }
        selectionSummary.textContent = parts.join(" - ");
      }

      if (plaqueMedium && variant) {
        plaqueMedium.textContent = variant.title;
      }

      updateMediaPreview();
      updateSubmitState();
    }

    materialInputs.forEach((input) => input.addEventListener("change", updatePrintOption));
    marcoInputs.forEach((input) => input.addEventListener("change", updatePrintOption));
    colorInputs.forEach((input) => input.addEventListener("change", updatePrintOption));
    sizeInputs.forEach((input) => input.addEventListener("change", updatePrintOption));
    extraInputs.forEach((input) => input.addEventListener("change", updatePrintOption));
    updatePrintOption();

    updateSubmitState();

    // Restore the customer's last saved boceto for this product, if any —
    // only on first load, before any fresh upload has happened this session.
    const savedBoceto = loadSavedBoceto();
    if (savedBoceto && !hiddenField.value) {
      hiddenField.value = savedBoceto.uploadId;
      hasUploadedPhoto = true;
      currentPreviewDataUrl = savedBoceto.previewDataUrl;

      if (savedBoceto.finish) {
        applyFinishValue(savedBoceto.finish);
      }
      if (savedBoceto.size) {
        const sizeInput = sizeInputs.find((el) => el.value === savedBoceto.size);
        if (sizeInput) sizeInput.checked = true;
      }

      // previewImg (the step-1 dropzone thumbnail) is meant to show the
      // customer's ORIGINAL uploaded photo — but a restored boceto only has
      // the generated preview saved, not the original file, so it's left
      // empty here rather than filled with the wrong image. The preview
      // itself is still shown via the gallery overlay / inline preview below
      // (both driven by currentPreviewDataUrl, set above).
      if (removeButton) removeButton.hidden = false;
      if (previewGenerateButton) previewGenerateButton.disabled = false;
      if (aiPreviewStatus) {
        aiPreviewStatus.textContent = t.bocetoRestored || "";
        aiPreviewStatus.className = "ronys-preview-status";
      }

      updatePrintOption(); // re-derives variant/price from the restored finish/size and refreshes the media preview
      updateSubmitState();
    }

    // Best-effort: a free, watermarked, lower-res preview so the customer can
    // gauge the result before buying. Never blocks add-to-cart — if it fails
    // (rate-limited, budget exhausted, network error) it just stays hidden.
    //
    // `previewRequestToken` guards against out-of-order responses: if the
    // customer uploads a second photo before the first preview finishes, the
    // late response from the first request must not overwrite the newer one.
    const PREVIEW_CACHE_KEY = "ronysLastPreviewDataUrl";
    const isPreviewDebugMode = /[?&]ronys_preview_debug=1/.test(window.location.search);

    // Replaces the main product gallery image with the customer's preview
    // once one exists — framed_* finishes show it composited into the
    // matching frame template (plain CSS overlay, no per-order generation —
    // see .ronys-media-preview-overlay__frame-photo in the stylesheet for
    // the measured window coordinates shared by all 4 frame templates);
    // other finishes show the flat preview. Before a preview exists, the
    // overlay stays hidden and the native gallery shows through underneath.
    function updateMediaPreview() {
      // Inline preview (under the generate-preview button) always just shows
      // the flat image — the frame compositing below is a desktop-gallery
      // nicety, not something this small in-form thumbnail needs.
      if (inlinePreviewWrap && inlinePreviewImg) {
        if (currentPreviewDataUrl) {
          inlinePreviewImg.src = currentPreviewDataUrl;
          inlinePreviewWrap.hidden = false;
        } else {
          inlinePreviewWrap.hidden = true;
        }
      }
      if (emailBocetoRow) {
        emailBocetoRow.hidden = !currentPreviewDataUrl;
      }

      if (!mediaOverlayWrap) return;
      if (!currentPreviewDataUrl) {
        mediaOverlayWrap.hidden = true;
        if (mediaWrapper) mediaWrapper.removeAttribute("data-ronys-preview-active");
        return;
      }

      const finishValue = computeFinishValue();
      const templateUrl = frameTemplates[finishValue];

      if (templateUrl) {
        if (mediaOverlayFrameImg) mediaOverlayFrameImg.src = templateUrl;
        if (mediaOverlayFramePhotoImg) mediaOverlayFramePhotoImg.src = currentPreviewDataUrl;
        if (mediaOverlayFrameStage) mediaOverlayFrameStage.hidden = false;
        if (mediaOverlayFlatImg) mediaOverlayFlatImg.hidden = true;
      } else {
        if (mediaOverlayFlatImg) {
          mediaOverlayFlatImg.src = currentPreviewDataUrl;
          mediaOverlayFlatImg.hidden = false;
        }
        if (mediaOverlayFrameStage) mediaOverlayFrameStage.hidden = true;
      }
      mediaOverlayWrap.hidden = false;
      if (mediaWrapper) mediaWrapper.setAttribute("data-ronys-preview-active", "");
    }

    // Cheap live "preview" of the personalization text (see the CSS overlay's
    // own comment in buy-buttons.liquid for why this is a CSS approximation,
    // not a canvas composite matching Pillow's real stamp pixel-for-pixel).
    // Driven purely by the text/checkbox/font inputs -- independent of
    // whether an AI preview image exists yet, since the overlay's parent
    // (.ronys-inline-preview) already handles its own hidden state.
    function updateTextOverlay() {
      if (!textOverlay) return;
      const noText = noTextInput ? noTextInput.checked : false;
      const name = petNameInput ? petNameInput.value.trim() : "";
      if (noText || !name) {
        textOverlay.hidden = true;
        return;
      }
      if (textOverlayName) textOverlayName.textContent = name;
      const dedication = dedicationInput ? dedicationInput.value.trim() : "";
      if (textOverlayDedication) {
        textOverlayDedication.textContent = dedication;
        textOverlayDedication.hidden = !dedication;
      }
      const font = fontInputs.find((el) => el.checked);
      textOverlay.classList.toggle("ronys-text-overlay--serif", !!font && font.value === "serif");
      textOverlay.hidden = false;
    }

    if (noTextInput) {
      noTextInput.addEventListener("change", () => {
        if (personalizeFields) personalizeFields.hidden = noTextInput.checked;
        if (personalizeCleanNote) personalizeCleanNote.hidden = !noTextInput.checked;
        updateTextOverlay();
      });
    }
    if (petNameInput) petNameInput.addEventListener("input", updateTextOverlay);
    if (dedicationInput) dedicationInput.addEventListener("input", updateTextOverlay);
    fontInputs.forEach((input) => input.addEventListener("change", updateTextOverlay));
    updateTextOverlay();

    function showPreviewLoading() {
      if (aiPreviewStatus) {
        aiPreviewStatus.textContent = t.previewLoading || "";
        aiPreviewStatus.className = "ronys-preview-status ronys-preview-status--loading";
      }
      if (previewGenerateButton) previewGenerateButton.disabled = true;
    }

    function showPreviewError(message) {
      if (aiPreviewStatus) {
        aiPreviewStatus.textContent = message || "";
        aiPreviewStatus.className = "ronys-preview-status ronys-preview-status--error";
      }
      if (previewGenerateButton) previewGenerateButton.disabled = false;
    }

    function showPreviewImage(src) {
      currentPreviewDataUrl = src;
      if (aiPreviewStatus) {
        aiPreviewStatus.textContent = "";
        aiPreviewStatus.className = "ronys-preview-status";
      }
      if (previewGenerateButton) previewGenerateButton.disabled = false;
      updateMediaPreview();
    }

    async function requestPreview(uploadId) {
      const requestToken = ++previewRequestToken;
      showPreviewLoading();

      // Debug mode: simulate the loading delay and reuse the last real preview
      // (cached in localStorage) instead of calling Gemini again — lets us
      // iterate on the loading UI without paying for generation each time.
      if (isPreviewDebugMode) {
        await new Promise((resolve) => setTimeout(resolve, 1800));
        if (requestToken !== previewRequestToken) return; // superseded by a newer request
        const cached = window.localStorage.getItem(PREVIEW_CACHE_KEY);
        const fallback = previewImg && previewImg.src ? previewImg.src : null;
        if (cached || fallback) {
          // Prefer a real cached AI preview; fall back to the raw uploaded
          // photo so the debug UI never looks blank while iterating on the
          // loading state design.
          showPreviewImage(cached || fallback);
        } else {
          showPreviewError(t.previewDebugNoPhoto || "");
        }
        return;
      }

      try {
        const response = await fetch(BACKEND_PREVIEW_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            upload_id: uploadId,
            style: portraitStyle,
            pet_name: petNameInput ? petNameInput.value.trim() : "",
            dedication: dedicationInput ? dedicationInput.value.trim() : "",
            text_font: (fontInputs.find((el) => el.checked) || {}).value || "sans",
            no_text: noTextInput ? noTextInput.checked : false,
          }),
        });
        if (requestToken !== previewRequestToken) return; // superseded by a newer request

        if (!response.ok) {
          showPreviewError(
            response.status === 429 ? (t.previewLimit || "") : ""
          );
          return;
        }
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onload = () => {
          if (requestToken !== previewRequestToken) return; // superseded while reading
          const dataUrl = reader.result;
          try {
            window.localStorage.setItem(PREVIEW_CACHE_KEY, dataUrl);
          } catch (err) {
            // localStorage full/unavailable — debug caching is optional, ignore.
          }
          saveBoceto(dataUrl);
          showPreviewImage(dataUrl);
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        if (requestToken !== previewRequestToken) return;
        showPreviewError("");
      }
    }

    fileInput.addEventListener("change", async () => {
      const file = fileInput.files[0];
      if (!file) return;

      hiddenField.value = "";
      hasUploadedPhoto = false;
      addedCurrentUploadToCart = false; // a new photo hasn't been added to the cart yet
      // Picking a real file after having clicked "complete without photo"
      // means they changed their mind -- clear the skip state so the two
      // never coexist.
      photoSkipped = false;
      if (photoPendingField) photoPendingField.value = '';
      if (skipPhotoNote) skipPhotoNote.hidden = true;
      updateSubmitState();
      statusEl.textContent = "";
      statusEl.className = "ronys-step__status";
      previewRequestToken += 1; // a new photo invalidates any in-flight preview
      currentPreviewDataUrl = null;
      updateMediaPreview();
      if (previewGenerateButton) previewGenerateButton.disabled = true;
      if (aiPreviewStatus) {
        aiPreviewStatus.textContent = "";
        aiPreviewStatus.className = "ronys-preview-status";
      }

      const dims = await getImageDimensions(file);

      if (dims && Math.min(dims.width, dims.height) < MIN_DIMENSION) {
        statusEl.textContent = t.tooSmall;
        statusEl.classList.add("ronys-step__status--error");
        return;
      }

      if (previewImg) {
        const reader = new FileReader();
        reader.onload = () => {
          previewImg.src = reader.result;
          previewImg.hidden = false;
          if (removeButton) removeButton.hidden = false;
        };
        reader.readAsDataURL(file);
      }

      if (dims && dims.width / dims.height > MAX_LANDSCAPE_RATIO) {
        statusEl.textContent = t.landscapeWarning;
        statusEl.classList.add("ronys-step__status--warning");
      }

      statusEl.textContent = (statusEl.textContent ? statusEl.textContent + " — " : "") + t.uploading;
      statusEl.className = "ronys-step__status ronys-step__status--uploading";

      try {
        const formData = new FormData();
        formData.append("file", file);
        if (portraitSpecies) formData.append("species", portraitSpecies);

        const response = await fetch(BACKEND_UPLOAD_URL, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          const detail = body.detail;
          const msg = detail === "photo_too_small" ? t.tooSmall
            : detail === "unsupported_format" ? t.defaultError
            : detail === "no_pet_detected" ? t.notPet
            : detail === "wrong_species" ? t.wrongSpecies
            : detail === "inappropriate_content" ? t.inappropriate
            : t.defaultError;
          throw new Error(msg);
        }

        const { upload_id } = await response.json();
        hiddenField.value = upload_id;
        statusEl.textContent = t.success;
        statusEl.className = "ronys-step__status ronys-step__status--success";
        hasUploadedPhoto = true;
        updateSubmitState();
        if (previewGenerateButton) previewGenerateButton.disabled = false;
      } catch (err) {
        statusEl.textContent = err.message;
        statusEl.className = "ronys-step__status ronys-step__status--error";
      }
    });

    // Additional pet photos for the multi-pet group portrait (see the
    // isMultipet block of consts above) -- the first pet's photo is still
    // the existing fileInput/hiddenField above, unchanged; each row added
    // here uploads independently to the same /uploads endpoint and
    // contributes its upload_id to `_extra_photo_upload_ids` (comma-joined),
    // read on the backend by extract_portrait_items (see shopify.py).
    if (isMultipet && multipetAddButton && multipetRowsContainer && extraUploadIdsField) {
      const extraUploadIds = [];
      let multipetRowCounter = 0;

      function syncExtraUploadIdsField() {
        extraUploadIdsField.value = extraUploadIds.filter(Boolean).join(",");
      }

      function addMultipetPhotoRow() {
        const rowIndex = multipetRowCounter;
        multipetRowCounter += 1;
        extraUploadIds[rowIndex] = null;

        const row = document.createElement("div");
        row.className = "ronys-multipet-row";

        const inputId = `ronys-multipet-photo-${rootEl.dataset.ronysProductId || "x"}-${rowIndex}`;
        const rowInput = document.createElement("input");
        rowInput.type = "file";
        rowInput.accept = "image/jpeg,image/png,image/webp";
        rowInput.className = "visually-hidden";
        rowInput.id = inputId;

        const rowLabel = document.createElement("label");
        rowLabel.setAttribute("for", inputId);
        rowLabel.className = "ronys-multipet-row__label button button--secondary";
        rowLabel.textContent = t.selectPhoto || "";

        const rowStatus = document.createElement("p");
        rowStatus.className = "ronys-multipet-row__status";

        const rowRemove = document.createElement("button");
        rowRemove.type = "button";
        rowRemove.className = "ronys-multipet-row__remove";
        rowRemove.setAttribute("aria-label", t.removePhoto || "");
        rowRemove.textContent = "×";
        rowRemove.addEventListener("click", () => {
          extraUploadIds[rowIndex] = null;
          syncExtraUploadIdsField();
          row.remove();
        });

        rowInput.addEventListener("change", async () => {
          const file = rowInput.files[0];
          if (!file) return;
          extraUploadIds[rowIndex] = null;
          syncExtraUploadIdsField();
          rowStatus.textContent = t.uploading || "";
          rowStatus.className = "ronys-multipet-row__status ronys-multipet-row__status--uploading";
          try {
            const formData = new FormData();
            formData.append("file", file);
            const response = await fetch(BACKEND_UPLOAD_URL, { method: "POST", body: formData });
            if (!response.ok) {
              const body = await response.json().catch(() => ({}));
              const detail = body.detail;
              const msg = detail === "photo_too_small" ? t.tooSmall
                : detail === "unsupported_format" ? t.defaultError
                : detail === "no_pet_detected" ? t.notPet
                : detail === "inappropriate_content" ? t.inappropriate
                : t.defaultError;
              throw new Error(msg);
            }
            const { upload_id } = await response.json();
            extraUploadIds[rowIndex] = upload_id;
            syncExtraUploadIdsField();
            rowStatus.textContent = t.success || "";
            rowStatus.className = "ronys-multipet-row__status ronys-multipet-row__status--success";
          } catch (err) {
            rowStatus.textContent = err.message || (t.defaultError || "");
            rowStatus.className = "ronys-multipet-row__status ronys-multipet-row__status--error";
          }
        });

        row.appendChild(rowLabel);
        row.appendChild(rowInput);
        row.appendChild(rowStatus);
        row.appendChild(rowRemove);
        multipetRowsContainer.appendChild(row);
      }

      multipetAddButton.addEventListener("click", addMultipetPhotoRow);
    }

    if (previewGenerateButton) {
      previewGenerateButton.addEventListener("click", () => {
        if (hiddenField.value) requestPreview(hiddenField.value);
      });
    }

    if (removeButton) {
      removeButton.addEventListener("click", () => {
        clearSavedBoceto();
        fileInput.value = "";
        hiddenField.value = "";
        if (previewImg) {
          previewImg.hidden = true;
          previewImg.src = "";
        }
        removeButton.hidden = true;
        statusEl.textContent = "";
        statusEl.className = "ronys-step__status";
        hasUploadedPhoto = false;
        updateSubmitState();
        previewRequestToken += 1; // discard any in-flight preview request
        currentPreviewDataUrl = null;
        updateMediaPreview();
        if (previewGenerateButton) {
          previewGenerateButton.hidden = true;
          previewGenerateButton.disabled = false;
        }
        if (aiPreviewStatus) {
          aiPreviewStatus.textContent = "";
          aiPreviewStatus.className = "ronys-preview-status";
        }
      });
    }

    // Any successful add-to-cart on the page (Dawn's AJAX product form fires
    // this pub/sub event) means the current upload is now safely in a cart line
    // item — "view cart" no longer risks losing anything.
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        addedCurrentUploadToCart = true;
      });
    }

    // Once the main product is in the cart, add any checked paid extras
    // (priority review / extra digital file) as their own cart lines, each
    // tagged with which portrait they belong to. These are hidden add-on
    // products (see buy-buttons.liquid's .ronys-extras block) that exist only
    // to carry a price + line item property -- they never carry
    // `_print_option`/`_photo_upload_id`, so Gelato's order processing
    // (shopify.py) skips them exactly like any other unrelated line item.
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined" && extraInputs.length > 0) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source !== "product-form") return;
        const checked = extraInputs.filter((el) => el.checked && !el.closest("[hidden]"));
        if (checked.length === 0) return;

        const associatedLabel = (petNameInput && petNameInput.value.trim()) || productTitle;
        const items = checked.map((el) => ({
          id: Number(el.dataset.ronysExtraVariantId),
          quantity: 1,
          properties: associatedLabel ? { "Retrato asociado": associatedLabel } : {},
        }));

        const cartElement = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        const body = { items };
        if (cartElement) {
          body.sections = cartElement.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        fetch(window.routes.cart_add_url, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              console.error("Failed to add extras to cart:", response.description || response.message);
              return;
            }
            checked.forEach((el) => {
              el.checked = false;
            });
            if (cartElement && response.sections) cartElement.renderContents(response);
          })
          .catch((error) => console.error("Failed to add extras to cart:", error));
      });
    }

    const viewCartLink = productRoot.querySelector("[data-ronys-view-cart]");
    const cartWarningModal = productRoot.querySelector("[data-ronys-cart-warning-modal]");
    const cartWarningContinue = productRoot.querySelector("[data-ronys-cart-warning-continue]");
    const cartWarningCancelEls = productRoot.querySelectorAll("[data-ronys-cart-warning-cancel]");

    if (viewCartLink && cartWarningModal && cartWarningContinue) {
      viewCartLink.addEventListener("click", (event) => {
        if (hasUploadedPhoto && !addedCurrentUploadToCart) {
          event.preventDefault();
          cartWarningModal.hidden = false;
        }
      });

      cartWarningContinue.addEventListener("click", () => {
        cartWarningModal.hidden = true;
        window.location.href = viewCartLink.href;
      });

      cartWarningCancelEls.forEach((el) => {
        el.addEventListener("click", () => {
          cartWarningModal.hidden = true;
        });
      });
    } else if (viewCartLink) {
      // Fallback in case the modal markup isn't present for some reason —
      // still guard against silently losing the customer's work.
      viewCartLink.addEventListener("click", (event) => {
        if (hasUploadedPhoto && !addedCurrentUploadToCart) {
          if (!window.confirm(t.viewCartWarning || "")) event.preventDefault();
        }
      });
    }

    // "Email me this design" — sends the already-generated free preview to an
    // address the customer types in, inline, no modal and no page navigation
    // (the button is type="button", never a form submit). Marketing automation
    // (abandoned-design reminders, discount codes) is intentionally NOT part
    // of this — see Pendientes.md for that separate, deferred piece.
    if (emailBocetoSubmit && emailBocetoInput) {
      function setEmailBocetoStatus(message, variant) {
        if (!emailBocetoStatus) return;
        emailBocetoStatus.textContent = message || "";
        emailBocetoStatus.hidden = !message;
        emailBocetoStatus.className = "ronys-email-boceto__status" + (variant ? ` ronys-email-boceto__status--${variant}` : "");
      }

      emailBocetoSubmit.addEventListener("click", async () => {
        if (!hiddenField.value) return;

        const email = emailBocetoInput.value.trim();
        const submitLabel = emailBocetoSubmit.textContent;
        emailBocetoSubmit.disabled = true;
        emailBocetoSubmit.textContent = t.emailBocetoSending || submitLabel;
        setEmailBocetoStatus("");

        try {
          const response = await fetch(BACKEND_PREVIEW_EMAIL_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              upload_id: hiddenField.value,
              style: portraitStyle,
              email,
              locale: rootEl.dataset.ronysLocale || "",
              pet_name: petNameInput ? petNameInput.value.trim() : "",
              dedication: dedicationInput ? dedicationInput.value.trim() : "",
              text_font: (fontInputs.find((el) => el.checked) || {}).value || "sans",
              no_text: noTextInput ? noTextInput.checked : false,
              continue_url: window.location.href,
            }),
          });

          if (response.status === 422) {
            setEmailBocetoStatus(t.emailBocetoInvalid || "", "error");
            return;
          }
          if (!response.ok) {
            setEmailBocetoStatus(t.emailBocetoError || "", "error");
            return;
          }

          emailBocetoInput.value = "";
          setEmailBocetoStatus(t.emailBocetoSuccess || "", "success");
        } catch (err) {
          setEmailBocetoStatus(t.emailBocetoError || "", "error");
        } finally {
          emailBocetoSubmit.disabled = false;
          emailBocetoSubmit.textContent = submitLabel;
        }
      });
    }
  }

  document.querySelectorAll('[data-ronys-photo-upload-widget]').forEach((rootEl) => {
    initPhotoUploadWidget(rootEl);
  });
})();
